// src/BarQueue.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, Button, Space, Tag, Typography, Empty, message, Divider, theme, Drawer, List, Badge } from "antd";
import { fetchPickupQueueDelta, pushOrderStatus, QueueItem, resetPickupSince } from "../services/posQueue";
import { getDeviceSettings } from "../services/deviceSettings";
import { logoutToPin } from "../services/session";
import { makeSnakeColumns } from "../components/layout/SnakeColumns";
 import { ClockCircleFilled } from "@ant-design/icons";

const { Text, Title } = Typography;

const LS_PREPARED_ITEMS = "pos.bar.prepared";
type PreparedMap = Record<string, number[]>;
type UndoEntry = { id: number; number: string; table?: string | number | null; created_at?: string; items: QueueItem["items"]; fromStatus: "delivered"; at: number; };
function loadPreparedMap(key: string): PreparedMap { try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : {}; } catch { return {}; } }
function savePreparedMap(key: string, map: PreparedMap) { try { localStorage.setItem(key, JSON.stringify(map)); } catch {} }
function getOrderPreparedSet(map: PreparedMap, orderId: number): Set<number> { const arr = map[String(orderId)] || []; return new Set(arr.filter((n) => Number.isFinite(n))); }
 function parseToMs(s?: string): number | null { if (!s) return null; const t = Date.parse(s); return Number.isFinite(t) ? t : null; }
function mmss(ms: number): string { const sec = Math.max(0, Math.floor(ms/1000)); const m=Math.floor(sec/60), s=sec%60; return `${m}:${s<10?`0${s}`:s}`; }

export default function BarQueue() {
  const { token } = theme.useToken();

  const [rows, setRows] = useState<QueueItem[]>([]);
  const [intervalMs, setIntervalMs] = useState(15000);
  const [lastSync, setLastSync] = useState<number | null>(null);

  const [prefCols, setPrefCols] = useState<number>(4);
  const [cardW, setCardW] = useState<number>(320);

  const labelsRef = useRef({ title: "Šank / Isporuka — narudžbe", table: "Sto", served: "Predato gostu", undo: "Vrati u ready", undoTitle: "Undo (predato ▶ ready)" , undoKitchen: "Vrati u kuhinju" });

  const [preparedMap] = useState<PreparedMap>(() => loadPreparedMap(LS_PREPARED_ITEMS));
  useEffect(() => savePreparedMap(LS_PREPARED_ITEMS, preparedMap), [preparedMap]);

  const [undoOpen, setUndoOpen] = useState(false);
  const [undoList, setUndoList] = useState<UndoEntry[]>([]);

  useEffect(() => {
    (async () => {
      const s = await getDeviceSettings([
        "auto_sync_sec",
        "lock_duration_sec",
        "bar_max_cols", "bar_card_width",
        "label_bar_title", "label_table", "label_served", "label_bar_undo", "label_bar_undo_title",
        "kitchen_max_cols", "kitchen_card_width",
      ]);
      const sec = Number(s["auto_sync_sec"] ?? 15);
      setIntervalMs(Math.max(4000, sec * 1000));
      const cols = Number(s["bar_max_cols"] ?? s["kitchen_max_cols"] ?? 4);
      const width = Number(s["bar_card_width"] ?? s["kitchen_card_width"] ?? 320);
      setPrefCols(clampInt(cols, 4, 1, 8));
      setCardW(clampInt(width, 320, 260, 520));

      labelsRef.current = {
        title: s["label_bar_title"] || labelsRef.current.title,
        table: s["label_table"] || labelsRef.current.table,
        served: s["label_served"] || labelsRef.current.served,
        undo: s["label_bar_undo"] || labelsRef.current.undo,
        undoTitle: s["label_bar_undo_title"] || labelsRef.current.undoTitle,
        undoKitchen: s["label_bar_undo_kitchen"] || labelsRef.current.undoKitchen,
      };
    })();
  }, []);

  const [cols, setCols] = useState<number>(calcColsFromWidth(cardW, 4));
  useEffect(() => {
    const onResize = () => setCols(calcColsFromWidth(cardW, prefCols));
    setCols(calcColsFromWidth(cardW, prefCols));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [cardW, prefCols]);

  useEffect(() => {
    let stop = false;
    async function tick() {
      try {
        const { rows: delta, serverTime } = await fetchPickupQueueDelta(true, ["ready"]);
        //filter only ready status
        const readyOrders = delta.filter((d) => d.status === "ready");
        if (readyOrders.length) setRows((prev) => mergeOrders(prev, readyOrders));
        setLastSync(serverTime);
      } catch { resetPickupSince(); }
    }
    (async () => {
      try {
        const init = await fetchPickupQueueDelta(true, ["ready"]);
        //filter only ready status
        const readyOrders = init.rows.filter((d) => d.status === "ready");
        if (readyOrders.length) setRows(readyOrders);
        setLastSync(init.serverTime);
      } catch { resetPickupSince(); }
      while (!stop) { await sleep(intervalMs); if (stop) break; await tick(); }
    })();
    return () => { stop = true; };
  }, [intervalMs]);

  const onServed = async (o: QueueItem) => {
    try {
      await pushOrderStatus(o.id, "delivered");
      setUndoList((prev) => [{ id:o.id, number:o.number, table:o.table, created_at:o.created_at, items:o.items, fromStatus:"delivered" as const, at: Math.floor(Date.now()/1000) }, ...prev].slice(0, 200));
      setRows((prev) => prev.filter((p) => p.id !== o.id));
    } catch { message.error("Nisam uspio označiti kao predato."); }
  };

  const onUndoServed = async (entry: UndoEntry) => {
    try {
      await pushOrderStatus(entry.id, "ready");
      setRows((prev) => {
        const back: QueueItem = { id: entry.id, number: entry.number, status: "ready", created_at: entry.created_at, items: entry.items, table: entry.table ?? null };
        return mergeOrders([back, ...prev], []);
      });
      setUndoList((prev) => prev.filter((u) => u.id !== entry.id));
      message.success("Vraćeno u ready.");
    } catch { message.error("Nisam uspio vratiti status."); }
  };
 const onUndoKitchen= async (entry: UndoEntry) => {
    try {
      await pushOrderStatus(entry.id, "in_preparation");
      setRows((prev) => {
        const back: QueueItem = { id: entry.id, number: entry.number, status: "in_preparation", created_at: entry.created_at, items: entry.items, table: entry.table ?? null };
        return mergeOrders([back, ...prev], []);
      });
      setUndoList((prev) => prev.filter((u) => u.id !== entry.id));
      message.success("Vraćeno u kuhinju.");
    } catch { message.error("Nisam uspio vratiti status."); }
  };

  //update elapsed

   useEffect(() => {
     //every 1 sec re-render
     const interval = setInterval(() => {
       setRows((prev) => prev.map((o) => {
         const createdMs = parseToMs(o.created_at);
         const elapsed = createdMs ? mmss(Date.now() - createdMs) : null;
         const prepSet = getOrderPreparedSet(preparedMap, o.id);
         return { ...o, elapsed, prepSet };
       }));
     }, 1000);
     return () => clearInterval(interval);
   }, [preparedMap]);

  const header = useMemo(() => {
    const timeStr = lastSync ? new Date(lastSync * 1000).toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" }) : "—";
    return (
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8, gap:12 }}>
        <Title level={4} style={{ margin:0, color: "#fff" }}>{labelsRef.current.title}</Title>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <Text type="secondary" style={{ opacity:.75 , color: "#fff" }}>delta: {Math.round(intervalMs/1000)}s • zadnje: {timeStr}</Text>
          <Badge count={undoList.length} size="small"><Button onClick={() => setUndoOpen(true)}>Undo</Button></Badge>
          <Button danger onClick={logoutToPin}>Odjava</Button>
        </div>
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, lastSync, token.colorText, undoList.length]);

  if (!rows.length) {
    return (
      <div style={{ padding: 16 }}>
        {header}
        <Empty description="Nema narudžbi za isporuku" />
        <UndoDrawer open={undoOpen} onClose={() => setUndoOpen(false)} list={undoList} labels={labelsRef.current} onUndo={onUndoServed} onUndoKitchen={onUndoKitchen} token={token} />
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      {header}

      <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8 ,paddingTop:20}}>
        {makeSnakeColumns(rows, cols).map((col, ci) => (
          <div key={ci} style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: cardW + 20 }}>
            {col.map((o) => {
              const createdMs = parseToMs(o.created_at);
              const elapsed = createdMs ? mmss(Date.now() - createdMs) : null;
              const prepSet = getOrderPreparedSet(preparedMap, o.id);

              return (
                <Card
                  key={o.id}
                  size="small"
                  style={{ borderRadius: 14, borderColor: o.status === "ready" ? "#10b981" : undefined }}
                  title={
                    <div style={{ display:"flex", alignItems:"baseline", gap:8, flexWrap:"wrap" }}>
                      <Text strong style={{ fontSize:16, color: token.colorText }}>#{o.number}</Text>
                      {o.table ? (
                        <Tag style={{ borderColor: token.colorBorderSecondary, color: token.colorTextSecondary, background: token.colorFillTertiary }}>
                          {labelsRef.current.table} {o.table}
                        </Tag>
                      ) : null}
                      {createdMs && (
                        <>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            <ClockCircleFilled /> {new Date(createdMs).toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" })}
                          </Text>

                          <Tag color="red" style={{position: "absolute", top:-10, right:-10, fontWeight: 700, fontSize: 18,zIndex:100, padding: "0 6px" }} title={createdMs ? `kreirano ${new Date(createdMs).toLocaleString()}` : undefined}>
                            {elapsed}
                          </Tag>
                        </>
                      )}
                    </div>
                  }
                  extra={<Tag color={o.status==="ready" ? "green" : "purple"} style={{ fontWeight:500 }}>{o.status}</Tag>}
                  styles={{ body: { paddingTop: 8, paddingBottom: 8 } }}
                >
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <div>
                      {o.items.map((it: any, idx) => {
                        const iid = Number(it.id ?? idx);
                        const qty = it.qty ?? it.quantity ?? 1;
                        const isPrepared = prepSet.has(iid);
                        return (
                          <div key={iid} style={{ marginBottom: 10 }}>
                            <div
                              onClick={() => {}}
                              style={{ cursor:"pointer", padding:"4px 6px", borderRadius:8, background: isPrepared ? token.colorFillTertiary : "transparent", transition:"background 0.15s" }}
                              title={isPrepared ? "Klikni da označiš kao 'nije gotovo'" : "Klikni da označiš kao 'spremno za predaju'"}
                            >
                              <div style={{ display:"flex", justifyContent:"space-between", gap:8, alignItems:"baseline" }}>
                                <Text strong style={{ fontSize:16, color:  isPrepared ? "red":token.colorText, opacity: isPrepared ? 0.5 : 1, textDecoration: isPrepared ? "line-through" : "none" }}>
                                  {qty}× {it.name}
                                </Text>
                              </div>

                              {it.modifiers?.length ? (
                                <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:4, marginTop:4 }}>
                                  {it.modifiers.map((m: string, i: number) => (
                                    <Tag key={i} style={{ borderColor: token.colorBorderSecondary, color: token.colorTextSecondary, background: token.colorFillSecondary, fontSize: 12, opacity: isPrepared ? 0.5 : 1 }}>
                                      {m}
                                    </Tag>
                                  ))}
                                </div>
                              ) : null}

                              {it.note ? (
                                <div style={{ marginTop: 4, padding:"6px 8px", borderRadius:8, background: token.colorWarningBg, border:`1px solid ${token.colorWarningBorder}`, opacity: isPrepared ? 0.5 : 1 }}>
                                  <Text style={{ fontStyle:"italic", color: token.colorText }}>{it.note}</Text>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <Divider style={{ margin: "6px 0" }} />
                    <Space><Button type="primary" onClick={() => onServed(o)}>{labelsRef.current.served}</Button></Space>
                  </Space>
                </Card>
              );
            })}
          </div>
        ))}
      </div>

      <UndoDrawer onUndoKitchen={onUndoKitchen} open={undoOpen} onClose={() => setUndoOpen(false)} list={undoList} labels={labelsRef.current} onUndo={onUndoServed} token={token} />
    </div>
  );
}

function UndoDrawer({ onUndoKitchen, open, onClose, list, labels, onUndo, token }: { onUndoKitchen: (u: UndoEntry) => void; open: boolean; onClose: () => void; list: UndoEntry[]; labels: { undoKitchen: string; undoTitle: string; undo: string; table: string; }; onUndo: (u: UndoEntry) => void; token: any; }) {
  return (
    <Drawer open={open} width={540} title={labels.undoTitle} onClose={onClose}>
      {!list.length ? <Empty description="Nema stavki za Undo" /> : (
        <List
          dataSource={list}
          renderItem={(u) => (
            <List.Item key={`${u.id}-${u.at}`} actions={[<Button  onClick={() => onUndo(u)}>{labels.undo}</Button>, <Button danger onClick={() => onUndoKitchen(u)}>{labels.undoKitchen}</Button>]}>
              <div style={{ width:"100%" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
                  <Text strong>#{u.number}</Text>
                  <Text type="secondary">{new Date(u.at*1000).toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" })}</Text>
                </div>
                {u.table ? (<div style={{ marginTop: 4, marginBottom: 8 }}><Tag style={{ borderColor: token.colorBorderSecondary, color: token.colorTextSecondary, background: token.colorFillTertiary }}>{labels.table} {u.table}</Tag></div>) : null}
                <div>
                  {u.items.map((it: any, i: number) => (
                    <div key={i} style={{ marginBottom: 6 }}>
                      {it.modifiers?.length ? (
                        <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:4 }}>
                          {it.modifiers.map((m: string, j: number) => <Tag key={j}>{m}</Tag>)}
                        </div>
                      ) : null}
                      <Text strong>{(it.qty ?? it.quantity ?? 1)}× {it.name}</Text>
                      {it.note ? (<div style={{ marginTop: 2, padding:"4px 6px", borderRadius:6, border:`1px solid ${token.colorBorderSecondary}` }}><Text style={{ fontStyle:"italic" }}>{it.note}</Text></div>) : null}
                    </div>
                  ))}
                </div>
              </div>
            </List.Item>
          )}
        />
      )}
    </Drawer>
  );
}

function sleep(ms: number) { return new Promise((r) => setTimeout(r, ms)); }
function clampInt(v: any, d: number, min: number, max: number) { const n = Number(v); return Number.isFinite(n) ? Math.max(min, Math.min(max, Math.round(n))) : d; }
function calcColsFromWidth(cardW: number, prefCols: number) { const w = typeof window !== "undefined" ? window.innerWidth : 1280; const maxByWidth = Math.max(1, Math.min(8, Math.floor(w/(cardW+20)))); return Math.min(maxByWidth, Math.max(1, Math.min(8, prefCols))); }
function mergeOrders(prev: QueueItem[], delta: QueueItem[]): QueueItem[] {
  const byId = new Map<number, QueueItem>(prev.map((o) => [o.id, o]));
  for (const d of delta) {
    if (!d.items || d.items.length === 0) { byId.delete(d.id); continue; }
    const old = byId.get(d.id);
    if (!old) byId.set(d.id, d);
    else byId.set(d.id, { ...old, ...d, items: d.items, status: d.status ?? old.status, created_at: d.created_at ?? old.created_at, ts: (d as any).ts ?? (old as any).ts });
  }
  const toTime = (s?: string) => (s ? Date.parse(s) || 0 : 0);
  return Array.from(byId.values()).sort((a,b)=>{ const ta=toTime(a.created_at), tb=toTime(b.created_at); return ta!==tb? ta-tb : a.id-b.id; });
}
